import { useTranslation } from '@hbs-us/ui';

import type { InventoryFieldDefinitionDto, InventoryItemDto } from './contractsMirror.js';

/**
 * Dynamic per-category custom-field renderer (the LOAD-BEARING piece of the inventory cutover).
 *
 * The inventory model is custom-per-category: each category owns a set of
 * `inventory_field_definitions`, and each item carries a `custom_fields` JSON map of
 * `{field_key: value}`. This component renders an item's custom field VALUES against its
 * category's field definitions — the definitions drive the labels + order; the item's map
 * supplies the values.
 *
 * SECURITY (T-22-xss — load-bearing for this plan): `custom_fields` values are USER-CONTROLLED
 * (a tenant types them in). They are rendered AS TEXT inside JSX, so React escapes them —
 * this component NEVER uses a raw-HTML-injection sink. A value like `<script>` renders as the
 * literal text `<script>`, not an executed element.
 *
 * Accessibility (A11Y-01): a semantic description list (`<dl>` with `<dt>`/`<dd>` pairs), so the
 * field labels are programmatically associated with their values. Every color is a locked token
 * (no hex, BRAND-06). When the category has no definitions, an empty-state line renders instead.
 */

export interface CustomFieldsRendererProps {
  item: InventoryItemDto;
  /** The field definitions for the item's category (drives labels + order). */
  fieldDefinitions: InventoryFieldDefinitionDto[];
}

export function CustomFieldsRenderer({ item, fieldDefinitions }: CustomFieldsRendererProps) {
  const { t } = useTranslation();

  if (fieldDefinitions.length === 0) {
    return <p className="text-muted-foreground text-sm">{t('inventory:detail.noCustomFields')}</p>;
  }

  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
      {fieldDefinitions.map((def) => {
        // The value comes from the item's custom_fields map keyed by the definition's field_key.
        // It is rendered AS TEXT below — React escapes it (T-22-xss). Never a raw-HTML sink.
        const rawValue = item.customFields[def.fieldKey];
        const hasValue = typeof rawValue === 'string' && rawValue.trim().length > 0;
        const display = hasValue
          ? def.unit
            ? `${rawValue} ${def.unit}`
            : rawValue
          : t('inventory:detail.noValue');

        return (
          <div key={def.id} className="flex flex-col gap-0.5">
            <dt className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
              {def.label}
            </dt>
            {/* TEXT child — React-escaped. The user-controlled value can never inject markup. */}
            <dd className="text-foreground text-sm">{display}</dd>
          </div>
        );
      })}
    </dl>
  );
}
